import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Alert,
  LinearProgress,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Container,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  CircularProgress,
  Skeleton,
  Snackbar
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  Image as ImageIcon,
  PhotoLibrary as PhotoLibraryIcon,
  CloudDownload as CloudDownloadIcon,
  Folder as FolderIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const MediaManager = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const [mediaData, setMediaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const previousLocationIdRef = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12;

  // Filtering and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [filteredMedia, setFilteredMedia] = useState([]);

  // UI state
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [previewDialog, setPreviewDialog] = useState({ open: false, media: null });
  const [uploadDialog, setUploadDialog] = useState({
    open: false,
    files: [],
    sourceUrl: '',
    uploading: false,
    category: 'ADDITIONAL',
    method: 'file',  // 'file', 'url', or 's3'
    s3Files: [],
    s3Loading: false,
    selectedS3Files: [],  // Array for multiple selection
    s3CurrentFolder: null,
    s3FolderStack: [],
    s3PreviewFile: null  // For image preview
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, media: null, deleting: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch media data
  const fetchMediaData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log(`🔄 Fetching media for location: ${locationId}`);
      const response = await fetch(`/api/media/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Media data fetched successfully');
      console.log(`📊 Total media items: ${data.data?.mediaItems?.length || 0}`);
      if (data.data?.mediaItems && data.data.mediaItems.length > 0) {
        const firstItem = data.data.mediaItems[0];
        console.log(`📸 First media item details:`, {
          name: firstItem.name?.split('/').pop(),
          format: firstItem.mediaFormat,
          hasThumbnail: !!firstItem.thumbnailUrl,
          hasProxyThumbnail: !!firstItem.proxyThumbnailUrl,
          hasSourceUrl: !!firstItem.sourceUrl,
          hasProxySourceUrl: !!firstItem.proxySourceUrl,
          thumbnailUrl: firstItem.thumbnailUrl,
          proxyThumbnailUrl: firstItem.proxyThumbnailUrl,
          sourceUrl: firstItem.sourceUrl
        });
      }
      setMediaData(data.data || {});
    } catch (err) {
      console.error('❌ Failed to fetch media:', err);
      setError(`Fehler beim Laden der Medien: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('📍 Location ID changed:', locationId);
    if (locationId && locationId !== previousLocationIdRef.current) {
      previousLocationIdRef.current = locationId;
      fetchMediaData();
    }
  }, [locationId]);

  // Filter & sort logic
  useEffect(() => {
    if (!mediaData?.mediaItems) {
      setFilteredMedia([]);
      return;
    }

    let filtered = [...mediaData.mediaItems];

    // Search
    if (searchTerm) {
      filtered = filtered.filter(item =>
        (item.name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(item =>
        item.locationAssociation?.category === filterCategory
      );
    }

    // Type filter (PHOTO/VIDEO)
    if (filterType !== 'all') {
      filtered = filtered.filter(item =>
        item.mediaFormat === filterType
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createTime || 0) - new Date(a.createTime || 0);
        case 'oldest':
          return new Date(a.createTime || 0) - new Date(b.createTime || 0);
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        default:
          return 0;
      }
    });

    setFilteredMedia(filtered);
    setCurrentPage(1);
  }, [mediaData, searchTerm, filterCategory, filterType, sortBy]);

  // Pagination
  const paginatedMedia = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredMedia.slice(start, end);
  }, [filteredMedia, currentPage]);

  const totalPages = Math.ceil(filteredMedia.length / ITEMS_PER_PAGE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Media actions
  const handlePreview = (media) => {
    setPreviewDialog({ open: true, media });
  };

  const handleUploadClick = () => {
    setUploadDialog({ ...uploadDialog, open: true });
  };

  const fetchS3Files = async (parentId = null) => {
    try {
      setUploadDialog(prev => ({ ...prev, s3Loading: true }));
      let url = `/api/businesses/${locationId}/library`;
      if (parentId) {
        url += `?parent=${parentId}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch library files: ${response.statusText}`);
      }

      const data = await response.json();
      // Extract files from nested response structure
      let files = [];
      if (Array.isArray(data)) {
        files = data;
      } else if (Array.isArray(data.rows?.rows?.documents)) {
        files = data.rows.rows.documents;
      } else if (Array.isArray(data.rows)) {
        files = data.rows;
      } else if (data.documents && Array.isArray(data.documents)) {
        files = data.documents;
      }

      console.log('📚 S3 Library items loaded:', files.length);
      setUploadDialog(prev => ({ ...prev, s3Files: files, s3Loading: false }));
    } catch (err) {
      console.error('❌ Failed to fetch S3 files:', err);
      setSnackbar({
        open: true,
        message: `Fehler beim Laden der Cloud-Dateien: ${err.message}`,
        severity: 'error'
      });
      setUploadDialog(prev => ({ ...prev, s3Loading: false }));
    }
  };

  const handleS3FolderOpen = (folder) => {
    setUploadDialog(prev => ({
      ...prev,
      s3FolderStack: [...prev.s3FolderStack, { id: prev.s3CurrentFolder, name: prev.s3CurrentFolder === null ? 'Bibliothek' : 'Zurück' }],
      s3CurrentFolder: folder._id
    }));
    fetchS3Files(folder._id);
  };

  const handleS3FolderBack = () => {
    setUploadDialog(prev => {
      if (prev.s3FolderStack.length === 0) return prev;
      const newStack = [...prev.s3FolderStack];
      const previousFolder = newStack.pop();
      const newCurrentFolder = previousFolder.id;

      // Fetch files for the previous folder
      setTimeout(() => fetchS3Files(newCurrentFolder), 0);

      return {
        ...prev,
        s3FolderStack: newStack,
        s3CurrentFolder: newCurrentFolder
      };
    });
  };

  const handleS3GoToRoot = () => {
    setUploadDialog(prev => ({
      ...prev,
      s3CurrentFolder: null,
      s3FolderStack: []
    }));
    fetchS3Files(null);
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    const minSizeKB = 10;
    const minSizeBytes = minSizeKB * 1024;
    const validFiles = [];
    const errors = [];

    selectedFiles.forEach(file => {
      if (file.size < minSizeBytes) {
        errors.push(`${file.name}: zu klein (mind. ${minSizeKB}KB)`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setSnackbar({
        open: true,
        message: `Fehler: ${errors.join(', ')}`,
        severity: 'error'
      });
    }

    if (validFiles.length > 0) {
      setUploadDialog(prev => ({ ...prev, files: [...prev.files, ...validFiles] }));
    }
  };

  const uploadSingleFile = async (fileToUpload, fileName) => {
    let uploadcareUrl;

    // Compress large images to fit under Vercel 5MB limit
    if (fileToUpload.size > 5 * 1024 * 1024) {
      console.log('🗜️ Image is large, compressing...');
      try {
        const canvas = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              // Reduce dimensions by 30% to reduce file size significantly
              canvas.width = img.width * 0.7;
              canvas.height = img.height * 0.7;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              resolve(canvas);
            };
            img.src = e.target.result;
          };
          reader.readAsDataURL(fileToUpload);
        });

        // Convert to blob with 70% quality
        const compressedBlob = await new Promise((resolve) => {
          canvas.toBlob(resolve, 'image/jpeg', 0.7);
        });

        const originalSizeMB = (fileToUpload.size / (1024 * 1024)).toFixed(2);
        const compressedSizeMB = (compressedBlob.size / (1024 * 1024)).toFixed(2);
        const reduction = ((1 - compressedBlob.size / fileToUpload.size) * 100).toFixed(1);

        console.log('✅ Image compressed:', {
          original: originalSizeMB + ' MB',
          compressed: compressedSizeMB + ' MB',
          reduction: reduction + '%'
        });

        // Create new File object from blob
        fileToUpload = new File([compressedBlob], fileName, { type: 'image/jpeg' });
      } catch (compressionError) {
        console.warn('⚠️ Compression failed, using original:', compressionError.message);
        // Continue with original if compression fails
      }
    }

    // Step 1: Upload to ImageKit via backend
    console.log('📤 Uploading to ImageKit:', fileToUpload.name, 'Size:', fileToUpload.size, 'Type:', fileToUpload.type);

    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);

      console.log('📡 Sending file to backend ImageKit upload endpoint...');

      const uploadResponse = await fetch('/api/imagekit/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      console.log('📋 Backend response status:', uploadResponse.status);

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('❌ Backend error response:', errorText);
        throw new Error(`Upload failed with status ${uploadResponse.status}: ${errorText}`);
      }

      const result = await uploadResponse.json();
      uploadcareUrl = result.url;
      const fileId = result.fileId;

      console.log('✅ File uploaded to ImageKit:', uploadcareUrl);
      console.log('📋 ImageKit response:', result);

      // Step 2: Send URL to backend (use URL method)
      console.log('📤 Uploading URL to GMB:', uploadcareUrl, 'Category:', uploadDialog.category);

      const response = await fetch(`/api/businesses/${locationId}/media`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sourceUrl: uploadcareUrl,
          category: uploadDialog.category
        })
      });

      const responseData = await response.json();
      console.log('📥 Upload response:', responseData);

      if (!response.ok) {
        const errorMessage = responseData?.error || responseData?.message || response.statusText;
        throw new Error(errorMessage);
      }

      const createSucceeded = responseData?.createSucceeded !== false;

      // Step 3: Delete file from ImageKit after successful GMB upload
      if (createSucceeded && fileId) {
        console.log('🗑️ Deleting file from ImageKit:', fileId);
        try {
          const deleteResponse = await fetch('/api/imagekit/delete', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ fileId })
          });

          if (deleteResponse.ok) {
            console.log('✅ File deleted successfully from ImageKit');
          } else {
            console.warn('⚠️ Failed to delete file from ImageKit:', await deleteResponse.text());
          }
        } catch (deleteErr) {
          console.warn('⚠️ Error deleting file from ImageKit:', deleteErr.message);
          // Don't throw, as the main upload already succeeded
        }
      }

      return createSucceeded;
    } catch (err) {
      console.error('❌ ImageKit upload failed:', err);
      throw err;
    }
  };

  const handleUpload = async () => {
    if (uploadDialog.method === 'file' && uploadDialog.files.length === 0) return;
    if (uploadDialog.method === 'url' && !uploadDialog.sourceUrl) return;
    if (uploadDialog.method === 's3' && uploadDialog.selectedS3Files.length === 0) return;

    try {
      setUploadDialog(prev => ({ ...prev, uploading: true }));

      if (uploadDialog.method === 'file') {
        // Upload multiple files
        let successCount = 0;
        let failureCount = 0;

        for (const file of uploadDialog.files) {
          try {
            const createSucceeded = await uploadSingleFile(file, file.name);
            if (createSucceeded) {
              successCount++;
            } else {
              failureCount++;
            }
          } catch (err) {
            console.error('Error uploading file:', err);
            failureCount++;
          }
        }

        // Show summary message
        if (successCount > 0) {
          setSnackbar({
            open: true,
            message: `${successCount} Datei${successCount > 1 ? 'en' : ''} erfolgreich hochgeladen! Die Medien werden in Kürze aktualisiert...`,
            severity: 'success'
          });
        }
        if (failureCount > 0) {
          setSnackbar({
            open: true,
            message: `${failureCount} Datei${failureCount > 1 ? 'en' : ''} fehlgeschlagen`,
            severity: 'error'
          });
        }
      } else if (uploadDialog.method === 'url') {
        // URL upload method (direct)
        console.log('📤 Uploading from URL:', uploadDialog.sourceUrl, 'Category:', uploadDialog.category);

        const response = await fetch(`/api/businesses/${locationId}/media`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            sourceUrl: uploadDialog.sourceUrl,
            category: uploadDialog.category
          })
        });

        const responseData = await response.json();
        if (!response.ok) {
          const errorMessage = responseData?.error || responseData?.message || response.statusText;
          throw new Error(errorMessage);
        }

        setSnackbar({
          open: true,
          message: 'URL erfolgreich hochgeladen! Die Medien werden in Kürze aktualisiert...',
          severity: 'success'
        });
      } else if (uploadDialog.method === 's3') {
        // S3 upload method - multiple files
        let successCount = 0;
        let failureCount = 0;

        for (const s3File of uploadDialog.selectedS3Files) {
          try {
            // Construct the full file URL from S3 bucket
            const fileUrl = s3File.url && s3File.key ? `${s3File.url}/${s3File.key}` : s3File.url;
            console.log('📚 Uploading from S3 bucket:', s3File.name, 'Category:', uploadDialog.category);
            console.log('📍 File URL:', fileUrl);

            const response = await fetch(`/api/businesses/${locationId}/media`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                sourceUrl: fileUrl,
                category: uploadDialog.category
              })
            });

            const responseData = await response.json();
            if (!response.ok) {
              const errorMessage = responseData?.error || responseData?.message || response.statusText;
              throw new Error(errorMessage);
            }

            successCount++;
          } catch (err) {
            console.error('Error uploading S3 file:', err);
            failureCount++;
          }
        }

        // Show summary message
        if (successCount > 0) {
          setSnackbar({
            open: true,
            message: `${successCount} Datei${successCount > 1 ? 'en' : ''} aus Cloud-Speicher erfolgreich hochgeladen! Die Medien werden in Kürze aktualisiert...`,
            severity: 'success'
          });
        }
        if (failureCount > 0) {
          setSnackbar({
            open: true,
            message: `${failureCount} Datei${failureCount > 1 ? 'en' : ''} fehlgeschlagen`,
            severity: 'error'
          });
        }
      }

      setUploadDialog({
        open: false,
        files: [],
        sourceUrl: '',
        uploading: false,
        category: 'ADDITIONAL',
        method: 'file',
        s3Files: [],
        s3Loading: false,
        selectedS3Files: [],
        s3CurrentFolder: null,
        s3FolderStack: [],
        s3PreviewFile: null
      });

      // Wait for Google to process and index the media
      console.log('⏳ Waiting for Google to process media...');

      // Try refreshing multiple times with increasing delays
      let mediaFound = false;
      for (let attempt = 1; attempt <= 5; attempt++) {
        const delayMs = attempt === 1 ? 3000 : 2000 * attempt;
        console.log(`⏳ Attempt ${attempt}: Waiting ${delayMs}ms before refresh...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));

        console.log(`🔄 Refreshing media list (attempt ${attempt}/5)...`);
        const checkResponse = await fetch(`/api/media/${locationId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (checkResponse.ok) {
          const checkData = await checkResponse.json();
          const itemCount = checkData.data?.mediaItems?.length || 0;
          console.log(`📊 Media items found: ${itemCount}`);

          if (itemCount > 0) {
            mediaFound = true;
            setMediaData(checkData.data || {});
            console.log(`✅ Media found after attempt ${attempt}!`);
            break;
          }
        }
      }

      if (!mediaFound) {
        console.warn(`⚠️ Media not yet appearing in list. Will be visible shortly.`);
        await fetchMediaData();
      }
    } catch (err) {
      console.error('❌ Upload error:', err);

      // Parse error message for user-friendly feedback
      let errorMessage = `Upload fehlgeschlagen: ${err.message}`;

      if (err.message?.includes('too small') || err.message?.includes('Image too small')) {
        errorMessage = '❌ Bild zu klein: Google Business erfordert Bilder mit mindestens 250×250 Pixeln. Wählen Sie ein größeres Bild.';
      } else if (err.message?.includes('not supported') || err.message?.includes('Image format')) {
        errorMessage = '❌ Bildformat nicht unterstützt. Verwenden Sie JPEG, PNG oder GIF.';
      } else if (err.message?.includes('Invalid argument')) {
        errorMessage = '❌ Ungültiges Bild. Stellen Sie sicher, dass die Datei gültig ist und die Google Business-Anforderungen erfüllt.';
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setUploadDialog(prev => ({ ...prev, uploading: false }));
    }
  };

  const handleDeleteClick = (media) => {
    setDeleteDialog({ open: true, media, deleting: false });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.media) return;

    try {
      setDeleteDialog(prev => ({ ...prev, deleting: true }));
      const mediaName = deleteDialog.media.name;
      const encodedName = encodeURIComponent(mediaName);

      const response = await fetch(
        `/api/businesses/${locationId}/media/${encodedName}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`);
      }

      setSnackbar({
        open: true,
        message: 'Datei erfolgreich gelöscht!',
        severity: 'success'
      });
      setDeleteDialog({ open: false, media: null, deleting: false });
      await fetchMediaData();
    } catch (err) {
      console.error('Delete error:', err);
      setSnackbar({
        open: true,
        message: `Fehler beim Löschen: ${err.message}`,
        severity: 'error'
      });
      setDeleteDialog(prev => ({ ...prev, deleting: false }));
    }
  };

  const handleDownload = async (media) => {
    const url = media.proxySourceUrl || media.sourceUrl || media.googleUrl;
    console.log('📥 Download requested for:', media.name?.split('/').pop());
    console.log('🔗 Available URLs:', {
      proxySourceUrl: !!media.proxySourceUrl,
      sourceUrl: !!media.sourceUrl,
      googleUrl: !!media.googleUrl,
      using: url ? (media.proxySourceUrl ? 'proxySourceUrl' : media.sourceUrl ? 'sourceUrl' : 'googleUrl') : 'none'
    });

    if (!url) {
      setSnackbar({
        open: true,
        message: 'Download URL not available for this media',
        severity: 'error'
      });
      return;
    }

    try {
      // For public CDNs (Google, ImageKit), don't send Authorization header
      // For local URLs, send the auth header
      const isPublicCDN = url.includes('lh3.googleusercontent.com') ||
                          url.includes('googleusercontent.com') ||
                          url.includes('imagekit.io') ||
                          url.includes('ik.imagekit.io');

      const fetchOptions = {};
      if (!isPublicCDN) {
        fetchOptions.headers = {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        };
      }

      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = media.name?.split('/').pop() || 'media';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      console.log('✅ Download started');
      setSnackbar({
        open: true,
        message: 'Download started successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('❌ Download error:', err);
      setSnackbar({
        open: true,
        message: `Download failed: ${err.message}`,
        severity: 'error'
      });
    }
  };

  const handleCopyLink = (media) => {
    const url = media.proxyThumbnailUrl || media.proxyGoogleUrl || media.googleUrl;
    if (url) {
      navigator.clipboard.writeText(url).then(() => {
        setSnackbar({
          open: true,
          message: 'Link in Zwischenablage kopiert!',
          severity: 'success'
        });
      });
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'PROFILE': 'Profilbild',
      'COVER': 'Titelbild',
      'LOGO': 'Logo',
      'EXTERIOR': 'Außenansicht',
      'INTERIOR': 'Innenansicht',
      'PRODUCT': 'Produkt',
      'AT_WORK': 'Bei der Arbeit',
      'FOOD_AND_DRINK': 'Essen & Trinken',
      'MENU': 'Menü',
      'COMMON_AREA': 'Gemeinschaftsbereich',
      'ROOMS': 'Räume',
      'TEAMS': 'Team',
      'ADDITIONAL': 'Zusätzlich'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'PROFILE': amDesignColors.primary,
      'COVER': '#9c27b0',
      'LOGO': amDesignColors.error,
      'EXTERIOR': amDesignColors.success,
      'INTERIOR': amDesignColors.warning,
      'PRODUCT': '#00bcd4',
      'AT_WORK': '#3f51b5',
      'FOOD_AND_DRINK': '#ff5722',
      'MENU': '#795548',
      'TEAMS': '#e91e63',
      'ADDITIONAL': '#607d8b'
    };
    return colors[category] || amDesignColors.text.tertiary;
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" sx={{ fontSize: '2rem', mb: 2, width: '300px' }} />
          <Skeleton variant="text" sx={{ fontSize: '1rem', mb: 3, width: '200px' }} />
        </Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={3} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" sx={{ fontSize: '1.5rem', mb: 2 }} />
                  <Skeleton variant="rectangular" width="100%" height={60} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2}>
          {[...Array(12)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card>
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <LinearProgress sx={{ borderRadius: 1, height: 6 }} />
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert
          severity="error"
          sx={{ borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchMediaData}>
              <RefreshIcon sx={{ mr: 1 }} />
              Erneut versuchen
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  // Calculate statistics
  const statistics = {
    totalMedia: mediaData?.mediaItems?.length || 0,
    photos: mediaData?.mediaItems?.filter(item => item.mediaFormat === 'PHOTO').length || 0,
    videos: mediaData?.mediaItems?.filter(item => item.mediaFormat === 'VIDEO').length || 0,
    profilePhotos: mediaData?.mediaItems?.filter(
      item => item.locationAssociation?.category === 'PROFILE'
    ).length || 0
  };

  return (
    <Container maxWidth="xl" >
      {/* Header */}
      <Paper
        
        sx={{
          p: 3,
          mb: 4,
          backgroundColor: amDesignColors.paper,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>

            <Typography
              variant="h4"
              sx={{
                mb: 0.5,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: amDesignColors.primary,
                fontFamily: amDesignTypography.fontFamily.heading
              }}
            >
              Verwalten Sie alle Bilder und Videos zentral
            </Typography>
            <Typography
              variant="h1"
              gutterBottom
              sx={{ fontWeight: 700, mb: 1, color: '#000000' }}
            >
              Medien-Management
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: amDesignColors.text.secondary,
                fontFamily: amDesignTypography.fontFamily.body
              }}
            >
              {selectedBusiness?.businessName || 'Ihr Unternehmen'}
            </Typography>
            
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleUploadClick}
              sx={{
                backgroundColor: '#C41E3A',
                '&:hover': { backgroundColor: '#A01729' },
                borderRadius: 2,
                px: 3
              }}
            >
              Hochladen
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchMediaData}
              sx={{
                borderRadius: 2,
                px: 3
              }}
            >
              Aktualisieren
            </Button>
          </Stack>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3}>
          {[
            { label: 'Gesamt', value: statistics.totalMedia },
            { label: 'Fotos', value: statistics.photos },
            { label: 'Videos', value: statistics.videos },
            { label: 'Profilbilder', value: statistics.profilePhotos, highlight: true }
          ].map((stat, i) => (
            <Grid item xs={6} md={3} key={i}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: '#FAFAFA',
                  border: stat.highlight ? '2px solid amDesignColors.warning' : '1px solid amDesignColors.border',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 0.5,
                    color: stat.highlight ? amDesignColors.warning : '#000000'
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" sx={{ color: '#605D5C', fontWeight: 500 }}>
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Medien durchsuchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Kategorie</InputLabel>
              <Select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} label="Kategorie">
                <MenuItem value="all">Alle Kategorien</MenuItem>
                <MenuItem value="PROFILE">Profilbild</MenuItem>
                <MenuItem value="COVER">Titelbild</MenuItem>
                <MenuItem value="LOGO">Logo</MenuItem>
                <MenuItem value="EXTERIOR">Außenansicht</MenuItem>
                <MenuItem value="INTERIOR">Innenansicht</MenuItem>
                <MenuItem value="PRODUCT">Produkt</MenuItem>
                <MenuItem value="AT_WORK">Bei der Arbeit</MenuItem>
                <MenuItem value="FOOD_AND_DRINK">Essen & Trinken</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Typ</InputLabel>
              <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} label="Typ">
                <MenuItem value="all">Alle Typen</MenuItem>
                <MenuItem value="PHOTO">Fotos</MenuItem>
                <MenuItem value="VIDEO">Videos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sortierung</InputLabel>
              <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label="Sortierung">
                <MenuItem value="newest">Neueste zuerst</MenuItem>
                <MenuItem value="oldest">Älteste zuerst</MenuItem>
                <MenuItem value="name">Name</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Chip label={`${filteredMedia.length} Ergebnisse`} color="primary" variant="outlined" />
          </Grid>
        </Grid>
      </Paper>

      {/* Media Grid */}
      {filteredMedia.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Keine Medien gefunden. Versuchen Sie, die Filter anzupassen oder neue Medien hochzuladen.
        </Alert>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {paginatedMedia.map((media) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={media.name}>
                <MediaCard
                  media={media}
                  onPreview={handlePreview}
                  onDownload={handleDownload}
                  onCopyLink={handleCopyLink}
                  onDelete={handleDeleteClick}
                  getCategoryLabel={getCategoryLabel}
                  getCategoryColor={getCategoryColor}
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
              <Paper elevation={1} sx={{ borderRadius: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
                  <Button disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                    Zurück
                  </Button>

                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let page;
                    if (totalPages <= 7) page = i + 1;
                    else if (currentPage <= 4) page = i + 1;
                    else if (currentPage > totalPages - 4) page = totalPages - 6 + i;
                    else page = currentPage - 3 + i;

                    if (page < 1 || page > totalPages) return null;

                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? 'contained' : 'text'}
                        onClick={() => handlePageChange(page)}
                        sx={{
                          minWidth: 40,
                          bgcolor: currentPage === page ? '#C41E3A' : 'transparent',
                          color: currentPage === page ? amDesignColors.white : 'text.primary',
                          '&:hover': {
                            bgcolor: currentPage === page ? '#a01729' : 'rgba(0,0,0,0.04)'
                          }
                        }}
                      >
                        {page}
                      </Button>
                    );
                  }).filter(Boolean)}

                  <Button disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                    Weiter
                  </Button>
                </Stack>
              </Paper>
            </Box>
          )}
        </>
      )}

      {/* Preview Modal */}
      <PreviewModal
        open={previewDialog.open}
        media={previewDialog.media}
        onClose={() => setPreviewDialog({ open: false, media: null })}
        onDownload={handleDownload}
        onDelete={handleDeleteClick}
        onCopyLink={handleCopyLink}
        getCategoryLabel={getCategoryLabel}
      />

      {/* Upload Dialog */}
      <UploadDialog
        open={uploadDialog.open}
        files={uploadDialog.files}
        sourceUrl={uploadDialog.sourceUrl}
        uploading={uploadDialog.uploading}
        category={uploadDialog.category}
        method={uploadDialog.method}
        s3Files={uploadDialog.s3Files}
        s3Loading={uploadDialog.s3Loading}
        selectedS3Files={uploadDialog.selectedS3Files}
        s3CurrentFolder={uploadDialog.s3CurrentFolder}
        s3FolderStack={uploadDialog.s3FolderStack}
        s3PreviewFile={uploadDialog.s3PreviewFile}
        onFileSelect={handleFileSelect}
        onSourceUrlChange={(url) => setUploadDialog({ ...uploadDialog, sourceUrl: url })}
        onMethodChange={(method) => {
          if (method === 's3') {
            handleS3GoToRoot();
            fetchS3Files(null);
          }
          setUploadDialog({ ...uploadDialog, method, files: [], sourceUrl: '', selectedS3Files: [] });
        }}
        onRemoveFile={(index) => setUploadDialog(prev => ({ ...prev, files: prev.files.filter((_, i) => i !== index) }))}
        onCategoryChange={(category) => setUploadDialog({ ...uploadDialog, category })}
        onAddS3File={(file) => {
          // Check if file already selected
          if (!uploadDialog.selectedS3Files.find(f => f._id === file._id)) {
            setUploadDialog(prev => ({ ...prev, selectedS3Files: [...prev.selectedS3Files, file] }));
          }
        }}
        onRemoveS3File={(fileId) => setUploadDialog(prev => ({
          ...prev,
          selectedS3Files: prev.selectedS3Files.filter(f => f._id !== fileId)
        }))}
        onPreviewS3File={(file) => setUploadDialog(prev => ({ ...prev, s3PreviewFile: file }))}
        onS3FolderOpen={handleS3FolderOpen}
        onS3FolderBack={handleS3FolderBack}
        onS3GoToRoot={handleS3GoToRoot}
        onUpload={handleUpload}
        onCancel={() => setUploadDialog({
          open: false,
          files: [],
          sourceUrl: '',
          uploading: false,
          category: 'ADDITIONAL',
          method: 'file',
          s3Files: [],
          s3Loading: false,
          selectedS3Files: [],
          s3CurrentFolder: null,
          s3FolderStack: [],
          s3PreviewFile: null
        })}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialog.open}
        media={deleteDialog.media}
        deleting={deleteDialog.deleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ open: false, media: null, deleting: false })}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Container>
  );
};

// Media Card Component
const MediaCard = ({
  media,
  onPreview,
  onDownload,
  onCopyLink,
  onDelete,
  getCategoryLabel,
  getCategoryColor
}) => {
  const isVideo = media.mediaFormat === 'VIDEO';
  // Prefer proxyThumbnailUrl which should have CORS headers, fall back to thumbnailUrl
  const thumbnailUrl = media.proxyThumbnailUrl || media.thumbnailUrl;
  const [imageError, setImageError] = useState(false);

  console.log(`🖼️ Media: ${media.name?.split('/').pop()}`, {
    hasProxyUrl: !!media.proxyThumbnailUrl,
    hasThumbnailUrl: !!media.thumbnailUrl,
    using: media.proxyThumbnailUrl ? 'proxyThumbnailUrl' : 'thumbnailUrl'
  });

  // Use placeholder immediately if no thumbnail URL
  const shouldShowPlaceholder = !thumbnailUrl || imageError;

  return (
    <Card
      sx={{
        height: '100%',
        border: 1,
        borderColor: 'divider',
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
          cursor: 'pointer'
        }
      }}
    >
      {/* Category Badge */}
      {media.locationAssociation?.category && (
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
          <Chip
            label={getCategoryLabel(media.locationAssociation.category)}
            size="small"
            sx={{
              bgcolor: getCategoryColor(media.locationAssociation.category),
              color: amDesignColors.white,
              fontWeight: 600
            }}
          />
        </Box>
      )}

      {/* Image/Video Thumbnail */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '100%',
          backgroundColor: 'grey.200',
          overflow: 'hidden'
        }}
      >
        {shouldShowPlaceholder ? (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.300'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <ImageIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
              <Typography variant="caption" color="textSecondary">
                {!thumbnailUrl ? 'No thumbnail' : `Loading...` }
              </Typography>
            </Box>
          </Box>
        ) : (
          <img
            src={media.thumbnailUrl}
            alt={media.name}
              crossOrigin="anonymous"   
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onLoad={() => {
              console.log(`✅ Image loaded: ${media.name?.split('/').pop()}`);
            }}
            onError={(e) => {
              console.warn(`⚠️ Image load error for ${media.name?.split('/').pop()}`);
              setImageError(true);
            }}
          />
        )}

        {/* Video Play Icon */}
        {isVideo && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'rgba(0,0,0,0.7)',
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PlayIcon sx={{ fontSize: 32, color: amDesignColors.white }} />
          </Box>
        )}
      </Box>

      <CardContent sx={{ p: 2 }}>
        {/* Media Info */}
        

        {/* Metadata */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" color="textSecondary">
            {media.mediaFormat || 'PHOTO'}
          </Typography>
          {media.dimensions && (
            <Typography variant="caption" color="textSecondary">
              {media.dimensions.widthPixels}×{media.dimensions.heightPixels}
            </Typography>
          )}
        </Box>

        {/* Date */}
        {media.createTime && (
          <Typography variant="caption" color="textSecondary">
            {format(parseISO(media.createTime), 'dd.MM.yyyy', { locale: de })}
          </Typography>
        )}
      </CardContent>

      {/* Action Buttons */}
      <Box sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Ansehen">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onPreview(media);
              }}
            >
              <PreviewIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Herunterladen">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDownload(media);
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Link kopieren">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onCopyLink(media);
              }}
            >
              <CopyIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {

        <Tooltip title="Löschen">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(media);
            }}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>


        }
      </Box>
    </Card>
  );
};

// Preview Modal Component
const PreviewModal = ({ open, media, onClose, onDownload, onDelete, onCopyLink, getCategoryLabel }) => {
  if (!media) return null;

  const isVideo = media.mediaFormat === 'VIDEO';
  const mediaUrl = media.proxySourceUrl || media.sourceUrl || media.googleUrl;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Medienvorschau</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {mediaUrl ? (
          isVideo ? (
            <video
              src={mediaUrl}
              controls
              style={{ width: '100%', maxHeight: '400px', objectFit: 'contain' }}
            />
          ) : (
            <img
              src={mediaUrl}
              alt={media.name}
                crossOrigin="anonymous"   
              style={{ width: '100%', maxHeight: '400px', objectFit: 'contain' }}
            />
          )
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="textSecondary">Medienvorschau nicht verfügbar</Typography>
          </Box>
        )}

        {/* Metadata */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Informationen
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Name</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {media.name?.split('/').pop() || 'Unnamed'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Typ</Typography>
              <Typography variant="body2">{media.mediaFormat}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Kategorie</Typography>
              <Typography variant="body2">
                {media.locationAssociation?.category ? getCategoryLabel(media.locationAssociation.category) : 'Keine'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Größe</Typography>
              <Typography variant="body2">
                {media.dimensions
                  ? `${media.dimensions.widthPixels}×${media.dimensions.heightPixels}`
                  : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">Erstellt</Typography>
              <Typography variant="body2">
                {media.createTime
                  ? format(parseISO(media.createTime), 'dd.MM.yyyy HH:mm', { locale: de })
                  : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<DownloadIcon />}
          onClick={() => {
            onDownload(media);
            onClose();
          }}
        >
          Herunterladen
        </Button>
        <Button
          startIcon={<CopyIcon />}
          onClick={() => {
            onCopyLink(media);
            onClose();
          }}
        >
          Link kopieren
        </Button>

        {/* 
        <Button
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => {
            onDelete(media);
            onClose();
          }}
        >
          Löschen
        </Button>

        */}
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

// Upload Dialog Component
const UploadDialog = ({
  open,
  files,
  sourceUrl,
  uploading,
  category,
  method,
  s3Files,
  s3Loading,
  selectedS3Files,
  s3CurrentFolder,
  s3FolderStack,
  s3PreviewFile,
  onFileSelect,
  onSourceUrlChange,
  onMethodChange,
  onRemoveFile,
  onCategoryChange,
  onAddS3File,
  onRemoveS3File,
  onPreviewS3File,
  onS3FolderOpen,
  onS3FolderBack,
  onS3GoToRoot,
  onUpload,
  onCancel
}) => {
  const categories = [
    { value: 'PROFILE', label: 'Profilbild' },
    { value: 'COVER', label: 'Titelbild' },
    { value: 'LOGO', label: 'Logo' },
    { value: 'EXTERIOR', label: 'Außenansicht' },
    { value: 'INTERIOR', label: 'Innenansicht' },
    { value: 'PRODUCT', label: 'Produkt' },
    { value: 'AT_WORK', label: 'Bei der Arbeit' },
    { value: 'FOOD_AND_DRINK', label: 'Essen & Trinken' },
    { value: 'MENU', label: 'Menü' },
    { value: 'ADDITIONAL', label: 'Zusätzlich' }
  ];

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Medien hochladen</DialogTitle>
      <DialogContent dividers>
        {/* Method selector */}
        <FormControl fullWidth size="small" sx={{ mb: 3 }}>
          <InputLabel>Upload-Methode</InputLabel>
          <Select
            value={method}
            onChange={(e) => onMethodChange(e.target.value)}
            label="Upload-Methode"
          >
            <MenuItem value="file">
              <UploadIcon sx={{ mr: 1, fontSize: 20 }} />
              Datei hochladen
            </MenuItem>
            <MenuItem value="url">
              <PhotoLibraryIcon sx={{ mr: 1, fontSize: 20 }} />
              URL eingeben
            </MenuItem>
            <MenuItem value="s3">
              <CloudDownloadIcon sx={{ mr: 1, fontSize: 20 }} />
              Aus Cloud-Speicher auswählen
            </MenuItem>
          </Select>
        </FormControl>

        {/* File upload method */}
        {method === 'file' && (
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': { borderColor: '#C41E3A', bgcolor: 'rgba(196, 30, 58, 0.05)' }
              }}
            >
              <input
                type="file"
                id="media-upload"
                hidden
                multiple
                onChange={onFileSelect}
                accept=".jpg,.jpeg,.png,.gif,.mp4,.mov,.webm"
              />
              <label htmlFor="media-upload" style={{ cursor: 'pointer', display: 'block' }}>
                <UploadIcon sx={{ fontSize: 48, color: '#C41E3A', mb: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {files.length > 0 ? `${files.length} Datei${files.length > 1 ? 'en' : ''} ausgewählt` : 'Dateien zum Hochladen auswählen'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  oder Dateien hierher ziehen
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                  Maximal 20 MB pro Datei (JPEG, PNG, GIF, MP4)
                </Typography>
              </label>
            </Box>

            {/* Display selected files */}
            {files.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Ausgewählte Dateien:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {files.map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        bgcolor: 'rgba(196, 30, 58, 0.08)',
                        borderRadius: 1,
                        border: '1px solid rgba(196, 30, 58, 0.2)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <ImageIcon sx={{ fontSize: 20, color: '#C41E3A' }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {file.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => onRemoveFile(index)}
                        sx={{ color: '#C41E3A' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* URL upload method */}
        {method === 'url' && (
          <TextField
            fullWidth
            label="Bild-URL"
            placeholder="https://example.com/image.jpg"
            value={sourceUrl}
            onChange={(e) => onSourceUrlChange(e.target.value)}
            size="small"
            sx={{ mb: 3 }}
            helperText="Geben Sie die vollständige URL zu dem Bild oder Video ein"
          />
        )}

        {/* S3 upload method */}
        {method === 's3' && (
          <Box sx={{ mb: 3 }}>
            {/* Breadcrumb Navigation - Always show if in a folder */}
            {s3CurrentFolder && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={onS3FolderBack}
                  variant="outlined"
                >
                  Zurück
                </Button>
                <Button
                  size="small"
                  onClick={onS3GoToRoot}
                  variant="text"
                >
                  Zur Bibliothek
                </Button>
              </Box>
            )}

            {s3Loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} />
                <Typography sx={{ ml: 2 }}>Cloud-Speicher wird geladen...</Typography>
              </Box>
            ) : !Array.isArray(s3Files) || s3Files.length === 0 ? (
              <Alert severity="info">Keine Dateien im Cloud-Speicher gefunden</Alert>
            ) : (
              <Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  {s3CurrentFolder ? 'Ordnerinhalte:' : 'Dateien und Ordner:'}
                </Typography>
                <Box sx={{
                  border: '1px solid amDesignColors.border',
                  borderRadius: 2,
                  maxHeight: '500px',
                  overflow: 'auto'
                }}>
                  {Array.isArray(s3Files) && s3Files.map((item) => {
                    const itemId = item._id || item.id;
                    const itemName = item.name || item.fileName || 'Untitled';
                    const isFolder = item.isFolder;
                    const fileType = item.mediaFormat || item.type || 'FILE';
                    const itemDate = item.createdAt || item.created_at || item.uploadedAt;
                    const isSelected = selectedS3Files.some(f => f._id === itemId);

                    if (isFolder) {
                      return (
                        <Box
                          key={itemId}
                          sx={{
                            p: 2,
                            borderBottom: '1px solid amDesignColors.border',
                            cursor: 'pointer',
                            backgroundColor: 'rgba(255, 152, 0, 0.05)',
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 152, 0, 0.15)'
                            }
                          }}
                          onClick={() => onS3FolderOpen(item)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <FolderIcon sx={{ color: amDesignColors.warning }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                                📁 {itemName}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Ordner
                              </Typography>
                            </Box>
                            <ArrowBackIcon sx={{ color: amDesignColors.warning, transform: 'rotate(180deg)' }} />
                          </Box>
                        </Box>
                      );
                    }

                    return (
                      <Box
                        key={itemId}
                        sx={{
                          p: 2,
                          borderBottom: '1px solid amDesignColors.border',
                          backgroundColor: isSelected ? 'rgba(196, 30, 58, 0.1)' : 'transparent',
                          borderLeft: isSelected ? '4px solid #C41E3A' : '4px solid transparent',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: 'rgba(196, 30, 58, 0.05)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          {/* Thumbnail Preview */}
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              flexShrink: 0,
                              backgroundColor: '#f5f5f5',
                              borderRadius: 1,
                              overflow: 'hidden',
                              border: '1px solid amDesignColors.border',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onClick={() => onPreviewS3File(item)}
                          >
                            {fileType === 'VIDEO' ? (
                              <Box sx={{ textAlign: 'center' }}>
                                <PlayIcon sx={{ fontSize: 40, color: '#C41E3A' }} />
                              </Box>
                            ) : item.url && item.key ? (
                              <Box
                                component="img"
                                src={`${item.url}/${item.key}`}
                                alt={itemName}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <ImageIcon sx={{ fontSize: 40, color: '#999' }} />
                            )}
                          </Box>

                          {/* File Info */}
                          <Box sx={{ flex: 1, cursor: 'pointer' }} onClick={() => onPreviewS3File(item)}>
                            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word', mb: 0.5 }}>
                              {itemName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {fileType} {itemDate ? `• ${new Date(itemDate).toLocaleDateString('de-DE')}` : ''}
                            </Typography>
                          </Box>

                          {/* Action Button */}
                          <Button
                            size="small"
                            variant={isSelected ? 'contained' : 'outlined'}
                            onClick={() => {
                              if (isSelected) {
                                onRemoveS3File(itemId);
                              } else {
                                onAddS3File(item);
                              }
                            }}
                            sx={{
                              backgroundColor: isSelected ? '#C41E3A' : 'transparent',
                              color: isSelected ? amDesignColors.white : '#C41E3A',
                              borderColor: '#C41E3A',
                              whiteSpace: 'nowrap',
                              flexShrink: 0
                            }}
                          >
                            {isSelected ? '✓' : '+'}
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {selectedS3Files.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {selectedS3Files.length} Datei{selectedS3Files.length > 1 ? 'en' : ''} ausgewählt
                    </Alert>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Ausgewählte Dateien:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {selectedS3Files.map((file) => (
                        <Box
                          key={file._id}
                          sx={{
                            p: 1.5,
                            bgcolor: 'rgba(196, 30, 58, 0.08)',
                            borderRadius: 1,
                            border: '1px solid rgba(196, 30, 58, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                            <ImageIcon sx={{ fontSize: 20, color: '#C41E3A' }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {file.name}
                              </Typography>
                            </Box>
                          </Box>
                          <Button
                            size="small"
                            onClick={() => onRemoveS3File(file._id)}
                            sx={{ color: '#C41E3A', minWidth: 'auto' }}
                          >
                            <CloseIcon fontSize="small" />
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Category selector */}
        <FormControl fullWidth size="small">
          <InputLabel>Kategorie</InputLabel>
          <Select
            value={category}
            onChange={(e) => onCategoryChange(e.target.value)}
            label="Kategorie"
          >
            {categories.map(cat => (
              <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button
          variant="contained"
          onClick={onUpload}
          disabled={
            (method === 'file' && files.length === 0) ||
            (method === 'url' && !sourceUrl) ||
            (method === 's3' && selectedS3Files.length === 0) ||
            uploading
          }
          sx={{ backgroundColor: '#C41E3A', '&:hover': { backgroundColor: '#A01729' } }}
        >
          {uploading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
        </Button>
      </DialogActions>

      {/* S3 File Preview Dialog */}
      {s3PreviewFile && (
        <Dialog
          open={!!s3PreviewFile}
          onClose={() => onPreviewS3File(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Dateivorschau</Typography>
            <IconButton onClick={() => onPreviewS3File(null)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ textAlign: 'center' }}>
              {s3PreviewFile.url && s3PreviewFile.key ? (
                <Box
                  component="img"
                  src={`${s3PreviewFile.url}/${s3PreviewFile.key}`}
                  alt={s3PreviewFile.name}
                  sx={{
                    width: '100%',
                    maxHeight: '400px',
                    objectFit: 'contain',
                    borderRadius: 1,
                    mb: 2
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <Typography color="textSecondary">Vorschau nicht verfügbar</Typography>
              )}
              <Box sx={{ mt: 2, textAlign: 'left' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Dateiinformationen
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Name:</strong> {s3PreviewFile.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Typ:</strong> {s3PreviewFile.mediaFormat || s3PreviewFile.type || 'FILE'}
                </Typography>
                {s3PreviewFile.createdAt && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Erstellt:</strong> {new Date(s3PreviewFile.createdAt).toLocaleDateString('de-DE')}
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onPreviewS3File(null)}>Schließen</Button>
            <Button
              variant="contained"
              onClick={() => {
                onAddS3File(s3PreviewFile);
                onPreviewS3File(null);
              }}
              sx={{ backgroundColor: '#C41E3A', '&:hover': { backgroundColor: '#A01729' } }}
            >
              + Hinzufügen
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
};

// Delete Confirmation Dialog Component
const DeleteConfirmDialog = ({ open, media, deleting, onConfirm, onCancel }) => {
  if (!media) return null;

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Datei löschen?</DialogTitle>
      <DialogContent>
        
        <Typography>
          Sind Sie sicher, dass Sie diese Datei löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={deleting}
        >
          {deleting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {deleting ? 'Wird gelöscht...' : 'Löschen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediaManager;
